import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { forgot_password_set_new_password } from '../../utils/authRequests';

// EyeIcon (same style as before)
const EyeIcon = ({ visible }) =>
  visible ? (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5.05 0-9.27-3.14-11-7.5a11.09 11.09 0 0 1 4.27-5.09M1 1l22 22" />
      <path d="M9.53 9.53A3.5 3.5 0 0 0 12 15.5a3.5 3.5 0 0 0 2.47-6M12 12" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12S5 4 12 4s11 8 11 8-4 8-11 8S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

// --- Validation helpers (kept minimal) ---
const MIN_LEN = 8;
const hasMinLen = (s) => (s?.length || 0) >= MIN_LEN;
const hasUpper = (s) => /[A-ZÅÄÖ]/.test(s);
const hasNumber = (s) => /\d/.test(s);

export const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [showTooltip, setShowTooltip] = useState(false);
  const iconRef = useRef(null);
  const tooltipRef = useRef(null);

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target) &&
        iconRef.current &&
        !iconRef.current.contains(e.target)
      ) {
        setShowTooltip(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Derived checks (memoized)
  const checks = useMemo(() => {
    const minLen = hasMinLen(password);
    const upper = hasUpper(password);
    const number = hasNumber(password);
    const match = confirmPassword.length > 0 && confirmPassword === password;
    return { minLen, upper, number, match };
  }, [password, confirmPassword]);

  const allValid = checks.minLen && checks.upper && checks.number && checks.match;

  const submitNewPassword = async () => {
    if (!token) {
      alert('Verifierings-token kunde inte hittas!');
      navigate('/login');
      return;
    }
    if (!allValid) {
      alert('Lösenordet uppfyller inte alla krav.');
      return;
    }
    try {
      const data = await forgot_password_set_new_password({ password, token });
      if (data.status === 'success') {
        alert('Ditt lösenord lyckades uppdateras!');
        navigate('/login');
      } else {
        alert(data.errorText || 'Något gick fel, försök igen.');
      }
    } catch (err) {
      console.error(err);
      alert('Ett fel uppstod. Försök igen.');
    }
  };

  // Small line item with subtle dot
  const Rule = ({ ok, text }) => (
    <div style={{ ...styles.ruleItem, opacity: ok ? 1 : 0.9 }}>
      <span style={{ ...styles.ruleDot, background: ok ? '#29d17f' : '#cccccc' }} />
      <span style={{ color: '#fff' }}>{text}</span>
    </div>
  );

  return (
    <div style={styles.pageSection}>
      <div style={styles.scrollContainer}>
        <div style={styles.resetPasswordContainer}>
          <h1 style={styles.title}>Skapa ditt nya lösenord</h1>

          {/* Info-ikon och tooltip with improved mobile positioning */}
          <div style={styles.infoWrapper}>
            <span
              ref={iconRef}
              style={styles.infoIcon}
              onClick={() => setShowTooltip(v => !v)}
              title="Klicka för att visa krav"
              role="button"
              aria-label="Visa lösenordskrav"
            >
              ℹ️
            </span>
            {showTooltip && (
              <div ref={tooltipRef} style={styles.tooltip}>
                <p>• Minst 8 tecken</p>
                <p>• Minst ett nummer</p>
                <p>• Minst en stor bokstav</p>
              </div>
            )}
          </div>

          <label style={styles.label}>Nytt lösenord</label>
          <div style={styles.passwordField}>
            <input
              style={styles.input}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Ange nytt lösenord"
            />
            <span
              style={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
              title={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
              role="button"
              aria-label={showPassword ? 'Dölj lösenord' : 'Visa lösenord'}
            >
              <EyeIcon visible={showPassword} />
            </span>
          </div>

          <label style={styles.label}>Repetera nytt lösenord</label>
          <div style={styles.passwordField}>
            <input
              style={styles.input}
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repetera lösenord"
            />
            <span
              style={styles.eyeIcon}
              onClick={() => setShowConfirm(!showConfirm)}
              title={showConfirm ? 'Dölj lösenord' : 'Visa lösenord'}
              role="button"
              aria-label={showConfirm ? 'Dölj lösenord' : 'Visa lösenord'}
            >
              <EyeIcon visible={showConfirm} />
            </span>
          </div>

          {/* Password validation rules */}
          <div style={styles.rulesBox}>
            <Rule ok={checks.minLen}  text="Minst 8 tecken" />
            <Rule ok={checks.number}  text="Minst ett nummer" />
            <Rule ok={checks.upper}   text="Minst en stor bokstav" />
            <Rule ok={checks.match}   text="Lösenorden matchar" />
          </div>

          <button
            style={{ ...styles.button, ...(allValid ? {} : styles.buttonDisabled) }}
            onClick={submitNewPassword}
            disabled={!allValid}
          >
            Skapa
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  pageSection: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #5DADE2, #1f4e8c)',
    overflow: 'hidden',
  },
  scrollContainer: {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start', // Changed to flex-start to allow scrolling
  },
  resetPasswordContainer: {
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '15px',
    boxShadow: '0 4px 30px rgba(0,0,0,0.1)',
    padding: '24px',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    maxWidth: '400px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    margin: '20px 0', // Added margin for better spacing when scrolling
  },
  title: {
    fontSize: 'clamp(1.5rem, 5vw, 1.8rem)', // Responsive font size
    textAlign: 'center',
    color: '#fff',
    marginBottom: '10px',
    marginTop: '0',
  },
  infoWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
    position: 'relative',
  },
  infoIcon: {
    fontSize: '1.3rem',
    cursor: 'pointer',
    color: '#fff',
    userSelect: 'none',
    padding: '5px',
  },
  tooltip: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: '#fff',
    color: '#333',
    padding: '10px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
    zIndex: 10,
    fontSize: '0.9rem',
    textAlign: 'left',
    width: 'max-content',
    maxWidth: '200px',
    marginTop: '5px',
  },
  label: {
    color: '#fff',
    fontSize: '1em',
    marginBottom: '8px',
    marginTop: '16px',
  },
  passwordField: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '14px 45px 14px 14px', // Added right padding for eye icon
    border: 'none',
    borderRadius: '6px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    fontSize: '1rem',
    boxSizing: 'border-box', // Ensure padding doesn't affect width
  },
  eyeIcon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    padding: '5px',
  },
  rulesBox: {
    marginTop: '16px',
    marginBottom: '10px',
    display: 'grid',
    gap: '8px',
  },
  ruleItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  ruleDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  button: {
    marginTop: '20px',
    backgroundColor: '#5DADE2',
    color: '#fff',
    border: 'none',
    padding: '15px',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease, opacity 0.2s ease',
    width: '100%',
    fontSize: '1rem',
    fontWeight: 600,
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default ResetPassword;